import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { BarChart, PieChart, Users, Download, ExternalLink, Search as SearchIcon, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import GoogleAnalyticsSetup from '@/components/GoogleAnalyticsSetup';
import { analyticsApi } from '@/services/analyticsApi';

const StatCard = ({ title, value, icon: Icon, sub, bgColor = 'bg-white' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className={`${bgColor} p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300`}
  >
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <Icon className="w-5 h-5 text-violet-500" />
    </div>
    <p className="text-3xl font-bold text-gray-800">{value}</p>
    {sub && <p className="text-xs mt-1 text-gray-500">{sub}</p>}
  </motion.div>
);

const ChartPlaceholder = ({ title, icon: Icon, description }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="bg-white p-6 rounded-xl shadow-lg"
  >
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
      <Icon className="w-6 h-6 text-violet-500" />
    </div>
    <div className="h-64 bg-gray-50 rounded-lg flex flex-col items-center justify-center text-center p-4">
      <Icon className="w-12 h-12 text-gray-400 mb-3" />
      <p className="text-gray-500">{description}</p>
    </div>
  </motion.div>
);

const COLORS = ['#6366f1', '#22c55e', '#0ea5e9', '#eab308', '#ec4899', '#8b5cf6', '#14b8a6', '#f97316'];

const GoogleAnalytics = () => {
  const { toast } = useToast();
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const res = await analyticsApi.getDashboard();
      setDashboard(res);
    } catch (err) {
      console.error('Error loading analytics dashboard:', err);
      setDashboardError(err.message || 'โหลดข้อมูลไม่สำเร็จ');
      setDashboard({ success: false, configured: false, data: null });
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleConnectAnalytics = () => {
    toast({
      title: 'กำลังเปลี่ยนเส้นทางไปยัง Google Analytics',
      description: 'คุณจะถูกนำไปยังหน้าเว็บ Google Analytics เพื่อทำการเชื่อมต่อบัญชีของคุณ',
    });
    window.open('https://analytics.google.com/', '_blank', 'noopener,noreferrer');
  };

  const handleExportReport = () => {
    toast({
      title: '🚧 ส่งออกรายงาน',
      description: 'ฟังก์ชันการส่งออกรายงานยังไม่ได้ถูกพัฒนา กรุณาแจ้งผู้พัฒนาหากต้องการใช้งานส่วนนี้ 🚀',
    });
  };

  const d = dashboard?.data;
  const configured = dashboard?.configured === true && d;
  const dailyChart = d?.dailyChart || [];
  const trafficSources = d?.trafficSources || [];
  const topKeywords = d?.topKeywords || [];

  return (
    <>
      <Helmet>
        <title>Google Analytics - ระบบจัดการ SACIT</title>
        <meta name="description" content="ภาพรวมสถิติการเข้าชมเว็บไซต์จาก Google Analytics" />
      </Helmet>
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Google Analytics</h1>
            <p className="text-gray-600 mt-1">ภาพรวมสถิติการเข้าชมเว็บไซต์ และการใช้งาน (ข้อมูลจาก GA4)</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleExportReport}>
              <Download className="w-4 h-4 mr-2" />
              ส่งออกรายงาน
            </Button>
            <Button variant="outline" onClick={loadDashboard} disabled={dashboardLoading}>
              โหลดใหม่
            </Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleConnectAnalytics}>
              <ExternalLink className="w-4 h-4 mr-2" />
              เปิด Google Analytics
            </Button>
          </div>
        </motion.div>

        <GoogleAnalyticsSetup />

        {dashboardError && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl">
            <p className="font-medium">ไม่สามารถโหลดข้อมูลจาก API</p>
            <p className="text-sm">{dashboardError}</p>
          </div>
        )}

        {dashboard?.configured === false && dashboard?.message && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-xl"
          >
            <p className="font-medium">ข้อมูลจาก GA4 ยังไม่ได้ตั้งค่า</p>
            <p className="text-sm">{dashboard.message}</p>
            <p className="text-xs mt-2 text-blue-600">
              ตั้งค่า GA4_PROPERTY_ID และ Service Account credentials ที่ backend แล้ว build/deploy ใหม่
            </p>
          </motion.div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {dashboardLoading ? (
            [...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-lg animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
                <div className="h-8 bg-gray-200 rounded w-3/4" />
              </div>
            ))
          ) : configured ? (
            <>
              <StatCard title="ผู้ใช้ทั้งหมด (30 วัน)" value={d.totalVisitors} icon={Users} sub="Active users" />
              <StatCard title="เซสชัน (30 วัน)" value={d.totalSessions} icon={BarChart} sub="Sessions" />
              <StatCard title="ผู้ใช้วันนี้" value={d.todayVisitors} icon={Users} sub="Today" />
              <StatCard title="การดูหน้า (30 วัน)" value={d.totalPageViews} icon={Download} sub="Page views" />
            </>
          ) : (
            <>
              <StatCard title="ผู้เข้าชมทั้งหมด" value="–" icon={Users} />
              <StatCard title="เซสชัน" value="–" icon={BarChart} />
              <StatCard title="ผู้เข้าชมวันนี้" value="–" icon={Users} />
              <StatCard title="การดูหน้า" value="–" icon={Download} />
            </>
          )}
        </div>

        {/* Chart row: Daily + Traffic pie */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {dashboardLoading ? (
              <div className="bg-white p-6 rounded-xl shadow-lg h-80 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
              </div>
            ) : configured && dailyChart.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-700 mb-4">ภาพรวมการเข้าชม (30 วันล่าสุด)</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsBarChart data={dailyChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(value) => [value, 'ผู้ใช้']}
                        labelFormatter={(label) => `วันที่ ${label}`}
                      />
                      <Bar dataKey="activeUsers" fill="#6366f1" name="ผู้ใช้" radius={[4, 4, 0, 0]} />
                    </RechartsBarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            ) : (
              <ChartPlaceholder
                title="ภาพรวมการเข้าชม (รายวัน)"
                icon={BarChart}
                description="กราฟจะแสดงเมื่อตั้งค่า GA4 Data API แล้ว"
              />
            )}
          </div>
          <div>
            {dashboardLoading ? (
              <div className="bg-white p-6 rounded-xl shadow-lg h-80 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
              </div>
            ) : configured && trafficSources.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <h3 className="text-lg font-semibold text-gray-700 mb-4">แหล่งที่มาของผู้เข้าชม</h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={trafficSources.map((s) => ({ name: s.name, value: s.sessions }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {trafficSources.map((_, index) => (
                          <Cell key={index} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, 'เซสชัน']} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            ) : (
              <ChartPlaceholder
                title="แหล่งที่มาของผู้เข้าชม"
                icon={PieChart}
                description="กราฟวงกลมจะแสดงเมื่อมีข้อมูลจาก GA4"
              />
            )}
          </div>
        </div>

        {/* Traffic sources bar list */}
        {configured && trafficSources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white p-6 rounded-xl shadow-lg"
          >
            <h3 className="text-xl font-semibold text-gray-700 mb-4">แหล่งที่มา (Channel) — จำนวนเซสชัน</h3>
            <div className="space-y-3">
              {trafficSources.map((source) => (
                <div key={source.name}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-600">{source.name}</span>
                    <span className="text-sm font-medium text-gray-600">
                      {source.sessions.toLocaleString()} ({source.value})
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full"
                      style={{
                        width: source.value,
                        backgroundColor: COLORS[trafficSources.indexOf(source) % COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Keywords */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-lg"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-700">คำค้นหา (Keywords)</h3>
            <div className="relative mt-3 sm:mt-0">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="ค้นหา Keyword..."
                className="pl-10 pr-4 py-2 w-full sm:w-64 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
                readOnly
              />
            </div>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {topKeywords.length > 0 ? (
              topKeywords.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-3 px-4 bg-gray-50 hover:bg-gray-100 rounded-lg"
                >
                  <p className="font-medium text-gray-700">{item.keyword || item.name}</p>
                  <p className="text-sm font-semibold text-violet-600">{item.count ?? item.sessions} ครั้ง</p>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">
                {configured
                  ? 'GA4 Data API ไม่ส่งข้อมูลคำค้นหาในรายงานนี้ (ใช้ดูได้จาก Google Analytics โดยตรง)'
                  : 'ยังไม่มีข้อมูลคำค้นหา'}
              </p>
            )}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChartPlaceholder
            title="ข้อมูลประชากรผู้เข้าชม"
            icon={Users}
            description="อายุ / เพศ — ดูได้จาก Google Analytics"
          />
          <ChartPlaceholder
            title="ตำแหน่งทางภูมิศาสตร์"
            icon={MapPin}
            description="แผนที่ — ดูได้จาก Google Analytics"
          />
        </div>
      </div>
    </>
  );
};

export default GoogleAnalytics;
